import { updateProfile } from "../ProfileSettingsPage/ProfileSettingsPage.duck";

// ================ Action types ================ //

export const ADD_TO_FAVORITES = 'app/FavouritePage/ADD_TO_FAVORITES';
export const REMOVE_FROM_FAVORITES = 'app/FavouritePage/REMOVE_FROM_FAVORITES';
export const TOGGLE_FAVORITE = 'app/FavouritePage/TOGGLE_FAVORITE';
export const UPDATE_FAVORITES_SUCCESS = 'app/FavouritePage/UPDATE_FAVORITES_SUCCESS';
export const UPDATE_FAVORITES_ERROR = 'app/FavouritePage/UPDATE_FAVORITES_ERROR';
export const LOAD_FAVORITES_FROM_PROFILE = 'app/FavouritePage/LOAD_FAVORITES_FROM_PROFILE';

// ================ Reducer ================ //

const initialState = {
    favorites: [],
    updateInProgress: false,
    updateError: null,
};

export default function favoritesReducer(state = initialState, action = {}) {
    const { type, payload } = action;

    switch (type) {
        case ADD_TO_FAVORITES: {
            const listingId = payload.listingId;
            if (state.favorites.includes(listingId)) {
                return state; 
            }
            return {
                ...state,
                favorites: [...state.favorites, listingId],
            };
        }

        case REMOVE_FROM_FAVORITES: {
            const listingId = payload.listingId;
            return {
                ...state,
                favorites: state.favorites.filter(id => id !== listingId),
            };
        }

        case TOGGLE_FAVORITE: {
            return {
                ...state,
                updateInProgress: true,
                updateError: null,
            };
        }

        case UPDATE_FAVORITES_SUCCESS: {
            return {
                ...state,
                updateInProgress: false,
                updateError: null,
            };
        }

        case UPDATE_FAVORITES_ERROR: {
            return {
                ...state,
                updateInProgress: false,
                updateError: payload.error,
            };
        }

        case LOAD_FAVORITES_FROM_PROFILE: {
            // This action is just for logging/debugging, actual data comes from selector
            return {
                ...state,
                updateError: null,
            };
        }

        default:
            return state;
    }
}

// ================ Action creators ================ //

export const addToFavorites = listingId => ({
    type: ADD_TO_FAVORITES,
    payload: { listingId },
});

export const removeFromFavorites = listingId => ({
    type: REMOVE_FROM_FAVORITES,
    payload: { listingId },
});

export const toggleFavoriteSuccess = () => ({
    type: UPDATE_FAVORITES_SUCCESS,
});

export const toggleFavoriteError = error => ({
    type: UPDATE_FAVORITES_ERROR,
    payload: { error },
});

export const loadFavoritesFromProfile = () => ({
    type: LOAD_FAVORITES_FROM_PROFILE,
});

// Thunk action to fetch favorite listings
export const fetchFavoriteListings = (favoriteIds) => (dispatch, getState, sdk) => {
    if (!favoriteIds || favoriteIds.length === 0) {
        return Promise.resolve();
    }

    const state = getState();
    const allListings = state.marketplaceData?.entities?.listing || {};
    const missingListings = favoriteIds.filter(id => !allListings[id]);

    if (missingListings.length === 0) {
        return Promise.resolve(); // All listings already loaded
    }

    console.log('Fetching missing favorite listings:', missingListings);

    // Import SDK types
    const { types: sdkTypes } = require('../../util/sdkLoader');
    const { UUID } = sdkTypes;

    const listingIds = missingListings.map(id => new UUID(id));

    return sdk.listings
        .query({
            ids: listingIds,
            include: ['author', 'images'],
            'fields.listing': [
                'title',
                'description',
                'price',
                'availabilityPlan',
                'publicData',
                'images',
            ],
            'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
            'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
        })
        .then(response => {
            const entities = response.data.data;
            const included = response.data.included || [];

            // Add listings to marketplace data
            const { addMarketplaceEntities } = require('../../ducks/marketplaceData.duck');
            dispatch(addMarketplaceEntities(entities, included));

            return entities;
        })
        .catch(error => {
            console.error('Error fetching favorite listings:', error);
            throw error;
        });
};

// Thunk action to toggle favorite and update profile
export const toggleFavorite = listingId => (dispatch, getState) => {
    const state = getState();
    const currentUser = state.user.currentUser;

    if (!currentUser) {
        dispatch(toggleFavoriteError('User not logged in'));
        return;
    }

    // Get current favorites from user's publicData (not local state)
    const currentFavorites = currentUser.attributes.profile.publicData.favorites || [];
    const isCurrentlyFavorite = currentFavorites.includes(listingId);

    // Calculate new favorites array
    const updatedFavorites = isCurrentlyFavorite
        ? currentFavorites.filter(id => id !== listingId)  // Remove from favorites
        : [...currentFavorites, listingId];                // Add to favorites

    // First update local state for immediate UI response
    dispatch({
        type: TOGGLE_FAVORITE,
        payload: { listingId },
    });

    // Prepare profile data with favorites in publicData
    const profileData = {
        publicData: {
            ...currentUser.attributes.profile.publicData,
            favorites: updatedFavorites,
        },
    };

    // Update profile with new favorites in SDK
    return dispatch(updateProfile(profileData))
        .then(() => {
            dispatch(toggleFavoriteSuccess());
            console.log('Favorites updated in user profile:', updatedFavorites);
        })
        .catch(error => {
            console.error('Error updating favorites in profile:', error);
            dispatch(toggleFavoriteError(error));
            // Revert local state on error
            dispatch({
                type: TOGGLE_FAVORITE,
                payload: { listingId },
            });
        });
};

// ================ Selectors ================ //

export const getFavorites = state => {
    // Always get from user's publicData (this persists across page refreshes)
    const currentUser = state.user.currentUser;
    if (currentUser && currentUser.attributes && currentUser.attributes.profile && currentUser.attributes.profile.publicData) {
        return currentUser.attributes.profile.publicData.favorites || [];
    }
    // Fallback to empty array if user not logged in
    return [];
};

export const isFavorite = (state, listingId) => {
    const favorites = getFavorites(state);
    return favorites.includes(listingId);
};

export const getFavoritesUpdateStatus = state => ({
    updateInProgress: state.FavouritePage.updateInProgress,
    updateError: state.FavouritePage.updateError,
});
